// @ts-strict-ignore
import { jsx } from 'slate-hyperscript';
import { useEffect, useState } from 'react';
import { Descendant } from 'slate';
import ReactDomServer from 'react-dom/server';
import { stripBBTags } from 'src/utils/string';
import { MarkAttributesProps } from 'src/components/textEditor/customTypes';
import { MentionedUser } from 'src/constants/types';
import { BBCODE, MENTIONED_USERS_ID } from 'src/constants/regex';
import { HIDDEN_USERNAME } from 'src/constants/hiddenUser';
import { getMention as getSimpleEditorMention } from 'src/utils/getElement/getMention';

export const addHTMLMentionsSimpleEditor = (text: string, mentionedUsers: MentionedUser[]) => {
  return text.replace(MENTIONED_USERS_ID, matched => {
    const mentionId = matched && matched.slice(2, -1); // @[username] -> username
    const userId = parseInt(mentionId);
    const username = mentionedUsers[userId]?.username || HIDDEN_USERNAME;
    // blank space before and after so that focus in the editor can be acquired properly.
    // last for on click anywhere right in editor, first for start of editor
    return '&nbsp' + ReactDomServer.renderToStaticMarkup(getSimpleEditorMention({ userId, username }, true)) + '&nbsp';
  });
};

const BBToHtmlMap = {
  '[b]': '<strong>',
  '[/b]': '</strong>',
  '[i]': '<em>',
  '[/i]': '</em>',
  '[u]': '<u>',
  '[/u]': '</u>',
};

const replaceBBWithHtml = text => {
  return text.replace(BBCODE, (matched => BBToHtmlMap[matched.toLowerCase()]));
};

// if your text is a bb string, first call this function to convert it to a html string
export const composeHtmlStringFromBB = ({ text, isTitle = false }: { text: string, isTitle?: boolean }) : string => {
  const htmlString = replaceBBWithHtml(text);
  const cleanHtml = htmlString && stripBBTags(htmlString);

  let wrappedWithParentElement;

  if (isTitle) {
    // title assumes editor has only 1 child - type 'title-medium'
    wrappedWithParentElement = '<TITLEMEDIUM>' + cleanHtml + '</TITLEMEDIUM>';
  } else {
    const paragraphArray = cleanHtml.split('\n\n');
    wrappedWithParentElement = paragraphArray.map((paragraph) => `<p>${paragraph}</p>`).join('');
  }

  return wrappedWithParentElement;
};

const addMentionTags = (text: string) => {
  // &nbsp must be present before [object Object] so that if it's the first elem of editor, there can be focus on path [0,0]
  return text.replace(MENTIONED_USERS_ID, matched => `&nbsp;<MENTION>${matched}</MENTION>`);
};

type UseDeserialiseHtmlStringProps = {
  text: string;
  mentionedUsers?: MentionedUser[];
  image?: { url: string, hash?: string, description?: string };
}

// this function is to be called on a html string (right now, we first convert bb to html. eventually, we are to store html strings)
export const useDeserialiseHtmlString = ({ text, mentionedUsers = null, image = null }: UseDeserialiseHtmlStringProps): Descendant[] => {
  const [deserialised, setDeserialised] = useState(null);

  // this needs to run AFTER document is defined
  useEffect(() => {
    const textWithMentions = mentionedUsers && addMentionTags(text) || text;
    // this is where we insert the img tag before the text
    const textWithMentionsAndImage = image?.url ? '<IMG/>' + textWithMentions : textWithMentions;
    const document = new DOMParser().parseFromString(textWithMentionsAndImage, 'text/html');
    const result = deserialise({ el: document.body, mentionedUsers, image });
    setDeserialised(result);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return deserialised;
};

type DeserialiseProps =  {
  el: Node;
  markAttributes?: MarkAttributesProps;
  mentionedUsers?: MentionedUser[];
  image?: { url: string, hash?: string, description?: string };
}

const deserialise = ({ el, markAttributes = {}, mentionedUsers, image = null }: DeserialiseProps) => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const nodeAttributes = { ...markAttributes };

  // Leafs:
  switch (el.nodeName) {
    case 'STRONG':
      nodeAttributes.bold = true;
      break;
    case 'EM':
      nodeAttributes.italic = true;
      break;
    case 'U':
      nodeAttributes.underline = true;
      break;
  }

  const children = Array.from(el.childNodes)
    .map(node => deserialise({ el: node, markAttributes: nodeAttributes, mentionedUsers, image }))
    .flat();

  if (children.length === 0) {
    children.push(jsx('text', nodeAttributes, ''));
  }

  // Elements:
  switch (el.nodeName) {
    case 'BODY':
      return jsx('fragment', {}, children);
    case 'P':
      return jsx('element', { type: 'paragraph' }, children);
    case 'TITLEMEDIUM':
      return jsx('element',  { type: 'title-medium' }, children);
    case 'MENTION': {
      const userId = children[0].text.slice(2, -1);
      if (!userId || !mentionedUsers?.[userId]) {
        return jsx('element', [{ text: '<could-not-load-mention>' }]); // this is for the sake of error catching ! should never appear.
      }
      return jsx('element',  { type: 'mention', mentionedUser: mentionedUsers[userId] }, [{ text: '' }]);
    }
    case 'IMG':
      return jsx('element',  { type: 'image', url: image?.url, hash: image?.hash, description: image?.description }, [{ text: '' }]);
    default:
      return children;
  }
};
